import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import {
  ToolAuthorization,
  ToolAuthorizationArgs,
  ToolAuthorizationContent,
  ToolAuthorizationHeader,
  ToolAuthorizationMessage,
} from "@arcadeai/design-system/components/ui/molecules/tool-authorization";
import { getToolkitIcon } from "@arcadeai/design-system/metadata/toolkit-icons";
import {
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  Loader2,
  PauseCircle,
  RefreshCw,
} from "lucide-react";
import { useEffect, useRef } from "react";
import type { AuthInfo } from "../../types";
import {
  getAutoOpenAttemptKey,
  getToolkitName,
  isSafeExternalUrl,
  openExternalUrl,
  type ToolAuthStatus,
} from "./utils";

const AUTH_STATUS_COPY = {
  pending: {
    title: (displayName: string) => `Authorize ${displayName}`,
    description: "Connect your account so the tool can continue.",
    message: (displayName: string) =>
      `Complete authorization in the ${displayName} tab, then the tool will continue.`,
    icon: (props: { className?: string }) => (
      <Loader2
        {...props}
        className={`animate-spin ${props.className ?? ""}`.trim()}
      />
    ),
  },
  completed: {
    title: (displayName: string) => `${displayName} connected`,
    description: "The tool can continue now.",
    message: (displayName: string) =>
      `${displayName} is ready. The tool will continue automatically.`,
    icon: CheckCircle2,
  },
  failed: {
    title: (displayName: string) => `${displayName} authorization failed`,
    description: "Reconnect your account to try again.",
    message: (displayName: string) =>
      `Could not authorize ${displayName}. Try again.`,
    icon: AlertCircle,
  },
  stopped: {
    title: (displayName: string) => `Stopped waiting for ${displayName}`,
    description:
      "Polling was stopped. You can finish authorization and run again when ready.",
    message: (displayName: string) =>
      `Stopped waiting for ${displayName} authorization. Reconnect and run again when you're ready.`,
    icon: PauseCircle,
  },
} satisfies Record<
  ToolAuthStatus,
  {
    title: (displayName: string) => string;
    description: string;
    message: (displayName: string) => string;
    icon: React.ComponentType<{ className?: string }>;
  }
>;

export function ToolAuthStatusCard({
  name,
  displayName,
  status,
  message,
  url,
  args,
  authInfo,
  onRetryAuth,
}: {
  name: string;
  displayName: string;
  status: ToolAuthStatus;
  message?: string;
  url?: string | null;
  args: Record<string, unknown>;
  authInfo?: AuthInfo | null;
  onRetryAuth?: () => void;
}) {
  const ToolkitIcon = getToolkitIcon(getToolkitName(name));
  const autoOpenAttemptKey = getAutoOpenAttemptKey(authInfo);
  const copy = AUTH_STATUS_COPY[status];
  const StatusIcon = copy.icon;
  const autoOpenedRef = useRef<string | null>(null);

  useEffect(() => {
    if (
      status !== "pending" ||
      !isSafeExternalUrl(url) ||
      !autoOpenAttemptKey
    ) {
      return;
    }

    if (autoOpenedRef.current !== autoOpenAttemptKey) {
      autoOpenedRef.current = autoOpenAttemptKey;
      openExternalUrl(url);
    }
  }, [status, url, autoOpenAttemptKey]);

  const dsStatus = status === "stopped" ? "failed" : status;

  return (
    <ToolAuthorization status={dsStatus}>
      <ToolAuthorizationHeader
        description={copy.description}
        icon={ToolkitIcon}
        status={dsStatus}
        title={copy.title(displayName)}
      />
      <ToolAuthorizationContent>
        <ToolAuthorizationMessage status={dsStatus}>
          <StatusIcon className="size-4 shrink-0" />
          {message ?? copy.message(displayName)}
        </ToolAuthorizationMessage>
        {(status === "pending" || status === "stopped") &&
          isSafeExternalUrl(url) && (
            <Button
              className="w-full"
              onClick={() => openExternalUrl(url)}
              size="sm"
              variant="default"
            >
              <ExternalLink className="size-4" />
              Open {displayName} authorization
            </Button>
          )}
        {(status === "failed" || status === "stopped") && onRetryAuth && (
          <Button
            className="w-full"
            onClick={onRetryAuth}
            size="sm"
            variant="outline"
          >
            <RefreshCw className="size-4" />
            Try again
          </Button>
        )}
        <ToolAuthorizationArgs args={args} />
      </ToolAuthorizationContent>
    </ToolAuthorization>
  );
}
